import { useEffect, useRef, useCallback } from 'react';

interface StreamColumn {
  x: number;
  speed: number;
  headY: number;
  chars: { y: number; char: string; opacity: number }[];
  maxLength: number;
}

interface DataBlock {
  x: number;
  y: number;
  w: number;
  h: number;
  opacity: number;
  phase: number;
}

interface DataMatrixVideoProps {
  color?: 'accent' | 'secondary';
  density?: number;
  className?: string;
}

const COLOR_MAP = {
  accent: { r: 112, g: 87, b: 255 },
  secondary: { r: 34, g: 211, b: 197 },
};

const CHARS = '01ABCDEF0123456789ABCDEF';

function randomChar(): string {
  return CHARS[Math.floor(Math.random() * CHARS.length)];
}

export default function DataMatrixVideo({
  color = 'accent',
  density = 1,
  className = '',
}: DataMatrixVideoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const streamsRef = useRef<StreamColumn[]>([]);
  const blocksRef = useRef<DataBlock[]>([]);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);

  const rgb = COLOR_MAP[color];

  const initStreams = useCallback((width: number, height: number) => {
    const colWidth = 22;
    const cols = Math.floor(width / colWidth);
    const count = Math.floor(cols * density * 0.5);
    const arr: StreamColumn[] = [];

    for (let i = 0; i < count; i++) {
      const colIndex = Math.floor(Math.random() * cols);
      const maxLen = 5 + Math.floor(Math.random() * 12);
      arr.push({
        x: colIndex * colWidth + Math.random() * 8,
        speed: 0.4 + Math.random() * 1.2,
        headY: Math.random() * height,
        chars: [],
        maxLength: maxLen,
      });
    }
    streamsRef.current = arr;
  }, [density]);

  const initBlocks = useCallback((width: number, height: number) => {
    const count = Math.floor(8 * density);
    const arr: DataBlock[] = [];
    const cols = 6;
    const rows = 4;
    const cellW = width / cols;
    const cellH = height / rows;

    for (let i = 0; i < count; i++) {
      const col = Math.floor(Math.random() * cols);
      const row = Math.floor(Math.random() * rows);
      arr.push({
        x: col * cellW + Math.random() * (cellW - 40) + 20,
        y: row * cellH + Math.random() * (cellH - 30) + 15,
        w: 24 + Math.random() * 20,
        h: 12 + Math.random() * 12,
        opacity: 0.03 + Math.random() * 0.08,
        phase: Math.random() * Math.PI * 2,
      });
    }
    blocksRef.current = arr;
  }, [density]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;
        initStreams(canvas.width, canvas.height);
        initBlocks(canvas.width, canvas.height);
      }
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const handleMouseLeave = () => {
      mouseRef.current = null;
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.016;

      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;

      // Set font for character rendering
      ctx.font = '11px "IBM Plex Mono", monospace';
      ctx.textBaseline = 'middle';

      // Draw data blocks in background
      blocksRef.current.forEach((block) => {
        block.phase += 0.015;
        const pulse = 0.5 + 0.5 * Math.sin(block.phase);
        const alpha = block.opacity * (0.6 + 0.4 * pulse);

        // Block fill
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
        ctx.fillRect(block.x, block.y, block.w, block.h);

        // Block border
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 1.5})`;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(block.x, block.y, block.w, block.h);

        // Tiny corner accent dots
        const dotAlpha = alpha * (0.8 + 0.2 * pulse);
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${dotAlpha})`;
        ctx.fillRect(block.x - 1, block.y - 1, 2, 2);
        ctx.fillRect(block.x + block.w - 1, block.y + block.h - 1, 2, 2);
      });

      // Draw character streams
      streamsRef.current.forEach((stream) => {
        // Move head down
        stream.headY += stream.speed;
        if (stream.headY > h + 60) {
          stream.headY = -20;
          stream.chars = [];
        }

        // Mouse interaction — streams near cursor slow down
        let effectiveSpeed = stream.speed;
        if (mouse) {
          const dx = mouse.x - stream.x;
          const dy = mouse.y - stream.headY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            effectiveSpeed = stream.speed * 0.2;
          }
        }

        // Add new char at head
        stream.chars.push({
          y: stream.headY,
          char: randomChar(),
          opacity: 0.35,
        });

        // Update and filter trail chars
        stream.chars = stream.chars
          .map((c) => ({
            ...c,
            opacity: c.opacity * 0.92,
          }))
          .filter((c) => c.opacity > 0.02 && c.y > -40);

        // Limit trail length
        if (stream.chars.length > stream.maxLength) {
          stream.chars = stream.chars.slice(-stream.maxLength);
        }

        // Draw trail
        stream.chars.forEach((c, idx) => {
          const isHead = idx === stream.chars.length - 1;
          const alpha = isHead ? 0.45 : c.opacity;
          const glowAlpha = isHead ? 0.25 : c.opacity * 0.4;

          // Character glow
          if (isHead) {
            const glow = ctx.createRadialGradient(stream.x, c.y, 0, stream.x, c.y, 12);
            glow.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${glowAlpha})`);
            glow.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(stream.x, c.y, 12, 0, Math.PI * 2);
            ctx.fill();
          }

          // Character
          ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
          ctx.fillText(c.char, stream.x - 5, c.y);
        });
      });

      // Horizontal scan lines (subtle)
      const scanAlpha = 0.015;
      const scanSpacing = 60;
      const scanOffset = (time * 40) % scanSpacing;
      for (let y = scanOffset; y < h; y += scanSpacing) {
        ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${scanAlpha})`;
        ctx.fillRect(0, y, w, 1);
      }

      // Intermittent data burst
      const burstActive = Math.sin(time * 0.7) > 0.85;
      if (burstActive) {
        const burstX = (Math.sin(time * 1.3) * 0.4 + 0.5) * w;
        const burstY = (Math.cos(time * 1.1) * 0.3 + 0.5) * h;
        const burstGlow = ctx.createRadialGradient(burstX, burstY, 0, burstX, burstY, 80);
        burstGlow.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.06)`);
        burstGlow.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03)`);
        burstGlow.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        ctx.fillStyle = burstGlow;
        ctx.beginPath();
        ctx.arc(burstX, burstY, 80, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animRef.current);
    };
  }, [color, density, rgb, initStreams, initBlocks]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
    />
  );
}